import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

const SMART_STICKY_HIDE_DISTANCE = 100;
const SMART_STICKY_SCROLL_TOLERANCE = 5;
const SMART_STICKY_TOP_TOLERANCE = 8;
const SMART_STICKY_TRANSITION_MS = 240;

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** @type {number | null} */
  #menuDrawerHiddenWidth = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {number} */
  #lastScrollTop = 0;

  /** @type {number} */
  #cumulativeDown = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {MediaQueryList} */
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  #setupSmartSticky = () => {
    if (this.getAttribute('sticky') !== 'scroll-up') return;

    const section = this.closest('.header-section');
    if (section instanceof HTMLElement) {
      section.style.position = 'sticky';
      section.style.top = '-1px';
      section.style.zIndex = 'var(--layer-menu-drawer)';
    }

    this.style.opacity = '1';
    this.style.willChange = 'transform';
    this.#applyMotionPreference();
    this.#showSmartSticky('inactive');
  };

  #applyMotionPreference = () => {
    if (this.getAttribute('sticky') !== 'scroll-up') return;

    this.style.transitionProperty = 'transform';
    this.style.transitionDuration = this.#reducedMotionQuery.matches ? '0ms' : `${SMART_STICKY_TRANSITION_MS}ms`;
    this.style.transitionTimingFunction = 'ease';
  };

  #handleMotionPreferenceChange = () => {
    this.#applyMotionPreference();
  };

  #isHeaderInteractionOpen = () => {
    if (
      this.querySelector(
        'header-menu[aria-expanded="true"], details[open], [aria-expanded="true"], .localization-wrapper:not([hidden])'
      )
    ) {
      return true;
    }

    return Boolean(
      document.querySelector(
        '#search-modal dialog[open], dialog#search-modal[open], #cart-drawer[open], theme-drawer[open]'
      )
    );
  };

  #showSmartSticky = (state = 'active') => {
    this.dataset.stickyState = state;
    this.style.opacity = '1';
    this.style.transform = 'translateY(0)';
    this.style.pointerEvents = '';
  };

  #hideSmartSticky = () => {
    this.dataset.stickyState = 'idle';
    this.style.opacity = '1';
    this.style.transform = 'translateY(calc(-100% - 2px))';
    this.style.pointerEvents = 'none';
  };

  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');

    if (stickyMode === 'scroll-up') {
      this.#lastScrollTop = Math.max(0, getScrollTop());
      this.#cumulativeDown = 0;
      this.#showSmartSticky(this.#lastScrollTop <= SMART_STICKY_TOP_TOLERANCE ? 'inactive' : 'active');
    }
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (stickyMode !== 'always' && stickyMode !== 'scroll-up') return;

    const scrollTop = Math.max(0, getScrollTop());
    const headerTop = this.getBoundingClientRect().top;
    const isAtTop = scrollTop <= SMART_STICKY_TOP_TOLERANCE || headerTop >= 0;

    if (stickyMode === 'always') {
      const isScrollingUp = scrollTop < this.#lastScrollTop;
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isAtTop) {
      this.#cumulativeDown = 0;
      this.#offscreen = false;
      this.dataset.scrollDirection = 'none';
      this.#showSmartSticky('inactive');
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (this.#isHeaderInteractionOpen()) {
      this.#cumulativeDown = 0;
      this.dataset.scrollDirection = 'none';
      this.#showSmartSticky('active');
      this.#lastScrollTop = scrollTop;
      return;
    }

    const delta = scrollTop - this.#lastScrollTop;

    if (Math.abs(delta) < SMART_STICKY_SCROLL_TOLERANCE) {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (delta < 0) {
      this.#cumulativeDown = 0;
      this.dataset.scrollDirection = 'up';
      this.#showSmartSticky('active');
    } else {
      this.dataset.scrollDirection = 'down';
      this.#cumulativeDown += delta;

      if (this.#cumulativeDown >= SMART_STICKY_HIDE_DISTANCE) {
        this.#hideSmartSticky();
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }

    if (stickyMode === 'scroll-up') {
      this.#lastScrollTop = Math.max(0, getScrollTop());
      this.#setupSmartSticky();
      this.#reducedMotionQuery.addEventListener('change', this.#handleMotionPreferenceChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#reducedMotionQuery.removeEventListener('change', this.#handleMotionPreferenceChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);

      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
